import React from 'react';
import {
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CCard,
  CCardBody,
  CCardTitle,
  CNav,
  CNavLink,
  CNavItem,
  CTabContent,
  CTabPane,
  CDateRangePicker,
  CButton,
  CSpinner,
  CSmartTable,
  CFormSelect,
  CRow,
  CCol,
  CFormInput,
  CModalFooter,
  CFormTextarea,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import {cilX} from '@coreui/icons';
import GaugeChart from '../shared/GaugeChart';
import BrokenSensor from '../shared/BrokenSensor';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import html2pdf from 'html2pdf.js/dist/html2pdf.min.js';
import {isMobile} from 'react-device-detect';
import styles from './WeatherStations.module.css';
import moment from 'moment';
import {
  hideWeatherStationModal,
  userSelectedHistoricalWeatherStartDate,
  userSelectedHistoricalWeatherEndDate,
  fetchHistoricalWeatherData,
  fetchWeatherStationHistoricalAverages,
  setSelectedYear,
  showReportWeatherStationModal,
  hideReportWeatherStationModal,
  setWeatherStationIssueCustomerName,
  setWeatherStationNameToReport,
  setWeatherStationIssueToReport,
  submitWeatherStationIssue,
} from './weatherStations-actions';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/export-data')(Highcharts);

// Highcharts plugin for adding better XLS and XLSX support through the third
// party zipcelx library.
// Code taken from online Highcharts support forums
// Import of 3rd party library in index.html of public folder
(function (H) {
  if (window.zipcelx && H.getOptions().exporting) {
    H.Chart.prototype.downloadXLSX = function () {
      var div = document.createElement('div'),
        name,
        xlsxRows = [],
        rows;
      div.style.display = 'none';
      document.body.appendChild(div);
      rows = this.getDataRows(true);
      xlsxRows = rows.slice(1).map(function (row) {
        return row.map(function (column) {
          return {
            type: typeof column === 'number' ? 'number' : 'string',
            value: column,
          };
        });
      });
      if (this.options.exporting.filename) {
        name = this.options.exporting.filename;
      }
      else if (this.title && this.title.textStr) {
        name = this.title.textStr.replace(/ /g, '-').toLowerCase();
      }
      else {
        name = 'chart';
      }
      window.zipcelx({
        filename: name,
        sheet: {
          data: xlsxRows
        }
      });
    };
    H.getOptions().lang.downloadXLSX = 'Download XLSX';
    H.getOptions().exporting.menuItemDefinitions.downloadXLSX = {
      textKey: 'downloadXLSX',
      onclick: function () {
        this.downloadXLSX();
      }
    };
    var menuItems = H.getOptions().exporting.buttons.contextButton.menuItems;
    menuItems[menuItems.indexOf('downloadXLS')] = 'downloadXLSX';

    // NOTRS Code Fragment - Remove unwanted export types from menu
    if (menuItems.length > 3) {
      menuItems.splice(1, 7);
      menuItems.splice(3, 1);
    }
  }
}(Highcharts));

const WeatherStationModal = () => {

  const dispatch = useDispatch();

  const {
    weatherStationModalVisible,
    selectedWeatherStation,
    //selectedWeatherStationSensorData,
    selectedWeatherStationHistoricalData,
    historicalStartDate,
    historicalEndDate,
    fetchingSensorData,
    fetchingHistoricalData,
    fetchingHistoricalAverages,
    selectedWeatherStationMonthlyHistoricalAverages,
    selectedWeatherStationWeeklyHistoricalAverages,
    years,
    selectedYear,
    reportWeatherStationModalVisible,
    weatherStationIssueToReport,
    weatherStationIssueCustomerName,
    weatherStationNameToReport,
  } = useSelector(state => state.weatherStations);

  const [activeTabIndex, setActiveTabIndex] = React.useState(0);

  const [isPdfView, setIsPdfView] = React.useState(false);

  const sensorsDivRef = React.useRef(null);

  const captureToPdf = () => {
    const opt = {
      filename: selectedWeatherStation?.stationName + ' - ' + moment().format("MM-DD-YYYY h_mmA") + '.pdf',
      margin: 0.1,
      image: {
        type: 'jpeg',
        quality: 1,
      },
      jsPDF: {
        unit: 'in',
        format: 'letter',
        orientation: 'landscape',
      },
      html2canvas: {
        scale: 2,
      },
    };
    html2pdf().set(opt).from(sensorsDivRef.current).save().then(() => setIsPdfView(false));
  };

  const monthlyColumns = [
    { key: 'month', label: 'Month' },
    { key: 'meanTemp', label: 'Average Temp (°F)' },
    { key: 'high', label: 'High (°F)' },
    { key: 'low', label: 'Low (°F)' },
    { key: 'precip', label: 'Rain (in)' },
    { key: 'avgST2Inch', label: 'Avg ST 1 Inch' },
    { key: 'avgST5Inch', label: 'Avg ST 4 Inch' },
    { key: 'expand', label: '' },
  ];
  
  const weeklyColumns = [
    { key: 'weekStarting', label: 'Week Starting' },
    { key: 'meanTemp', label: 'Average Temp (°F)' },
    { key: 'high', label: 'High (°F)' },
    { key: 'low', label: 'Low (°F)' },
    { key: 'precip', label: 'Rain (in)' },
    { key: 'avgST2Inch', label: 'Avg ST 1 Inch' },
    { key: 'avgST5Inch', label: 'Avg ST 4 Inch' },
  ];

  const [expandedMonths, setExpandedMonths] = React.useState([]);

  const toggleMonth = (month) => {
    setExpandedMonths((prev) =>
      prev.includes(month)
        ? prev.filter((m) => m !== month) 
        : [...prev, month] 
    );
  };

  const scopedColumns = {
    expand: (item) => {
      const isExpanded = expandedMonths.includes(item.month);
      return (
        <td>
          <CButton
            color='primary'
            variant='outline'
            onClick={() => toggleMonth(item.month)}>
            {isExpanded ? 'Hide Weeks' : 'Show Weeks'}
          </CButton>
          {isExpanded && (
            <CSmartTable
              items={selectedWeatherStationWeeklyHistoricalAverages.filter((week) => week.month === item.month)}
              columns={weeklyColumns}
              tableProps={{responsive: true,}}
            />
          )}
        </td>
      );
    },
  };

  return (
    <>
      <CModal
        fullscreen
        alignment='center'
        scrollable
        visible={weatherStationModalVisible}>
        <CModalHeader closeButton={false}>
          <CModalTitle className={styles.modalTitle}>
            {selectedWeatherStation?.stationName}
          </CModalTitle>
          <CButton className={styles.modalCloseButton} onClick={() => dispatch(hideWeatherStationModal())}>
            <CIcon className={styles.modalCloseButtonIcon} icon={cilX} />
          </CButton>
        </CModalHeader>
        <CModalBody>
          <CNav variant='pills' role='tablist' layout='fill'>
            <CNavItem>
              <CNavLink
                href='#!'
                active={activeTabIndex === 0}
                onClick={(event) => {
                  event.preventDefault();
                  setActiveTabIndex(0);
                }}>
                Current
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                href='#!'
                active={activeTabIndex === 1}
                onClick={(event) => {
                  event.preventDefault();
                  setActiveTabIndex(1);
                }}>
                Historical
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                href='#!'
                active={activeTabIndex === 2}
                onClick={(event) => {
                  event.preventDefault();
                  setActiveTabIndex(2);
                }}>
                Historical Averages
              </CNavLink>
            </CNavItem>
          </CNav>
          <CTabContent>
            <CTabPane role='tabpanel' visible={activeTabIndex === 0}>
              <br />
              <div>
                <div ref={sensorsDivRef}>
                  <div className={styles.sensorsTabTitleDiv}>
                    { 
                      isPdfView === true ? (
                        <CCardTitle className={styles.sensorsTabTitle}>
                          {selectedWeatherStation?.stationName}
                        </CCardTitle>
                      ) : null
                    }
                    <CCardTitle className={styles.sensorsTabTitle}>
                      {
                        selectedWeatherStation?.observationDateTimeFormatted ?
                          'Weather Data as of ' + selectedWeatherStation?.observationDateTimeFormatted :
                          ''
                      }
                      {
                        fetchingSensorData === true ? (
                          <CSpinner color='primary' />
                        ) : null
                      }
                    </CCardTitle>
                  </div>
                  <div className={isMobile === true ? styles.columnFlexDiv : styles.rowFlexDiv}>
                    <CCard className={styles.sensorCard}>
                      <CCardBody>
                        <div>
                          {
                            selectedWeatherStation?.temperature === null || selectedWeatherStation?.temperature < -40 || selectedWeatherStation?.temperature > 140 ? (
                              <BrokenSensor sensorName='temperature' />
                            ) : (
                              <GaugeChart
                                chartTitle='Temperature'
                                seriesData={[selectedWeatherStation?.temperature]}
                                seriesValueSuffix='Degrees'
                                maxValue={140} />
                            )
                          }
                        </div>
                      </CCardBody>
                    </CCard>
                    {
                      isMobile === true ? (<><br/></>) : null
                    }
                    <CCard className={styles.sensorCard}>
                      <CCardBody>
                        <div>
                          {
                            selectedWeatherStation?.windSpeed === null || selectedWeatherStation?.windSpeed < 0 || selectedWeatherStation?.windSpeed > 140 ? (
                              <BrokenSensor sensorName='wind speed' />
                            ) : (
                              <GaugeChart
                                chartTitle='Wind Speed'
                                seriesData={[selectedWeatherStation?.windSpeed]}
                                seriesValueSuffix={'MPH from ' + selectedWeatherStation?.windDirection}
                                maxValue={140} />
                            )
                          }
                        </div>
                      </CCardBody>
                    </CCard>
                    {
                      isMobile === true ? (<><br/></>) : null
                    }
                    <CCard className={styles.sensorCard}>
                      <CCardBody>
                        <div>
                          {
                            selectedWeatherStation?.rainfall24Hours === null || selectedWeatherStation?.rainfall24Hours < 0 || selectedWeatherStation?.rainfall24Hours > 30 ? (
                              <BrokenSensor sensorName='rain' />
                            ) : (
                              <GaugeChart
                                chartTitle='Rain Last 24 Hours'
                                seriesData={[selectedWeatherStation?.rainfall24Hours]}
                                seriesValueSuffix={selectedWeatherStation?.rainfallLastMonth !== null ? ('In. / (' + selectedWeatherStation?.rainfallLastMonth + ' In. Monthly)') : 'In.'}
                                maxValue={selectedWeatherStation?.rainfall24Hours < 11 ? 10 : selectedWeatherStation?.rainfall24Hours < 30 ? 20 : 30} />
                            )
                          }
                        </div>
                      </CCardBody>
                    </CCard>
                  </div>
                  <br />
                  <div className={isMobile === true ? styles.columnFlexDiv : styles.rowFlexDiv}>
                    <CCard className={styles.sensorCard}>
                      <CCardBody>
                        <div>
                          {
                            selectedWeatherStation?.humidity === null || selectedWeatherStation?.humidity < 0 || selectedWeatherStation?.humidity > 100 ? (
                              <BrokenSensor sensorName='humidity' />
                            ) : (
                              <GaugeChart
                                chartTitle='Humidity'
                                seriesData={[selectedWeatherStation?.humidity]}
                                seriesValueSuffix='%'
                                maxValue={100} />
                            )
                          }
                        </div>
                      </CCardBody>
                    </CCard>
                    {
                      isMobile === true ? (<><br/></>) : null
                    }
                    <CCard className={styles.sensorCard}>
                      <CCardBody>
                        <div>
                          {
                            selectedWeatherStation?.heatIndex === null || selectedWeatherStation?.heatIndex < 0 || selectedWeatherStation?.heatIndex > 140 ? (
                              <BrokenSensor sensorName='heat index' />
                            ) : (
                              <GaugeChart
                                chartTitle='Heat Index'
                                seriesData={[selectedWeatherStation?.heatIndex]}
                                seriesValueSuffix='Degrees'
                                maxValue={140} />
                            )
                          }
                        </div>
                      </CCardBody>
                    </CCard>
                    {
                      isMobile === true ? (<><br/></>) : null
                    }
                    <CCard className={styles.sensorCard}>
                      <CCardBody>
                        <div>
                          {
                            selectedWeatherStation?.barometerAbsolute === null || selectedWeatherStation?.barometerAbsolute < 0 || selectedWeatherStation?.barometerAbsolute > 60 ? (
                              <BrokenSensor sensorName='heat index' />
                            ) : (
                              <GaugeChart
                                chartTitle='Barometer'
                                seriesData={[selectedWeatherStation?.barometerAbsolute]}
                                seriesValueSuffix='Hg'
                                maxValue={60} />
                            )
                          }
                        </div>
                      </CCardBody>
                    </CCard>
                  </div>
                </div>
                <br />
                <div className={styles.saveAsPDFDiv}>
                  <CButton
                    color='primary'
                    variant='outline'
                    onClick={() => {
                      setIsPdfView(true);
                      captureToPdf();
                    }}>
                    Download PDF 
                  </CButton>
                </div>
              </div>
            </CTabPane>
            <CTabPane role='tabpanel' visible={activeTabIndex === 1}>
              <br />
              <div className={styles.rowFlexDiv}>
                <CCard className={styles.historicalCard}>
                  <CCardBody>
                    <div className={styles.rowFlexDiv}>
                      <CDateRangePicker
                        locale='en-US'
                        size='lg'
                        startDate={historicalStartDate}
                        endDate={historicalEndDate}
                        onStartDateChange={(date) => dispatch(userSelectedHistoricalWeatherStartDate(date))}
                        onEndDateChange={(date) => dispatch(userSelectedHistoricalWeatherEndDate(date))} />
                      <CButton
                        color='primary'
                        variant='outline'
                        onClick={() => dispatch(fetchHistoricalWeatherData())}
                        disabled={fetchingHistoricalData}>
                        {
                          fetchingHistoricalData === true ? (
                            <CSpinner size='sm' />
                          ) : null
                        }
                        {fetchingHistoricalData === true ? ' Loading' : 'Load Historical Data'}
                      </CButton>
                    </div>
                    <br />
                    <div>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={{
                          chart: {
                            type: 'line',
                          },
                          title: {
                            text: 'Historical Data',
                          },
                          exporting: {
                            enabled: true,
                            buttons: {
                              contextButton: {
                                text: 'Download Data',
                              },
                            },
                          },
                          xAxis: {
                            categories: selectedWeatherStationHistoricalData.observationDateTimeFormatted,
                            type: 'category',
                            tickInterval: 7,
                          },
                          yAxis: [
                            {
                              labels: {
                                format: '{value}°F',
                                style: {
                                  color: Highcharts.getOptions().colors[0]
                                }
                              },
                              title: {
                                text: 'Temperature (°F)',
                                style: {
                                  color: Highcharts.getOptions().colors[0]
                                }
                              },
                              opposite: false
                            },
                            {
                              title: {
                                text: 'Humidity',
                                style: {
                                  color: Highcharts.getOptions().colors[1]
                                }
                              },
                              labels: {
                                format: '{value}%',
                                style: {
                                  color: Highcharts.getOptions().colors[1]
                                }
                              },
                              opposite: false
                            },
                            {
                              title: {
                                text: 'Barometer',
                                style: {
                                  color: Highcharts.getOptions().colors[2]
                                }
                              },
                              labels: {
                                format: '{value} Hg',
                                style: {
                                  color: Highcharts.getOptions().colors[2]
                                }
                              },
                              opposite: false
                            },
                            {
                              title: {
                                text: 'Heat Index (°F)',
                                style: {
                                  color: Highcharts.getOptions().colors[3]
                                }
                              },
                              labels: {
                                format: '{value}°F',
                                style: {
                                  color: Highcharts.getOptions().colors[3]
                                }
                              },
                              opposite: true
                            },
                            {
                              title: {
                                text: 'Wind Speed',
                                style: {
                                  color: Highcharts.getOptions().colors[4]
                                }
                              },
                              labels: {
                                format: '{value} MPH',
                                style: {
                                  color: Highcharts.getOptions().colors[4]
                                }
                              },
                              opposite: true
                            },
                            {
                              title: {
                                text: 'Rain (inches)',
                                style: {
                                  color: Highcharts.getOptions().colors[5]
                                }
                              },
                              labels: {
                                format: '{value} Inches',
                                style: {
                                  color: Highcharts.getOptions().colors[5]
                                }
                              },
                              opposite: true
                            },
                          ],
                          plotOptions: {
                            line: {
                              dataLabels: {
                                enabled: false
                              },
                              enableMouseTracking: true
                            }
                          },
                          series: [
                            {
                              name: 'Temperature',
                              data: selectedWeatherStationHistoricalData.temperature,
                              yAxis: 0,
                              tooltip: {
                                valueSuffix: '°F'
                              },
                              visible: true,
                            },
                            {
                              name: 'Humidity',
                              data: selectedWeatherStationHistoricalData.humidity,
                              yAxis: 1,
                              tooltip: {
                                valueSuffix: '%'
                              },
                              visible: false,
                            },
                            {
                              name: 'Barometer',
                              data: selectedWeatherStationHistoricalData.barometerAbsolute,
                              yAxis: 2,
                              tooltip: {
                                valueSuffix: ' Hg'
                              },
                              visible: false,
                            },
                            {
                              name: 'Heat Index',
                              data: selectedWeatherStationHistoricalData.heatIndex,
                              yAxis: 3,
                              tooltip: {
                                valueSuffix: '°F'
                              },
                              visible: false,
                            },
                            {
                              name: 'Wind Speed',
                              data: selectedWeatherStationHistoricalData.wind,
                              yAxis: 4,
                              tooltip: {
                                pointFormatter: function () {
                                  var symbol = null;
                                  switch ( this.series.symbol ) {
                                    case 'circle':
                                      symbol = '&#9679';
                                      break;
                                    case 'diamond':
                                      symbol = '&#9670';
                                      break;
                                    case 'square':
                                      symbol = '&#9632';
                                      break;
                                    case 'triangle':
                                      symbol = '&#9650';
                                      break;
                                    case 'triangle-down':
                                      symbol = '&#9660';
                                      break;
                                    default:
                                      symbol = '&#9679';
                                      break;
                                  }
                                  return '<span style="color:' + this.series.color + ';">' + symbol + '</span> Wind: ' + this.y + ' MPH from ' + this.options.direction;
                                },
                              },
                              visible: true,
                            },
                            {
                              name: 'Rain (Inches)',
                              type: 'column',
                              data: selectedWeatherStationHistoricalData.rain,
                              yAxis: 5,
                              tooltip: {
                                valueSuffix: ' Inches'
                              },
                              visible: false,
                            },
                          ],
                        }} />
                    </div>
                  </CCardBody>
                </CCard>
              </div>
            </CTabPane>
            <CTabPane role='tabpanel' visible={activeTabIndex === 2}>
              <br />
              <div className={styles.rowFlexDiv}>
                <CCard className={styles.historicalCard}>
                  <CCardBody>
                    <div className={styles.rowFlexDiv}>
                      <CFormSelect
                        style={{width: 100,}}
                        value={selectedYear}
                        onChange={(e) => dispatch(setSelectedYear(e.target.value))}>
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </CFormSelect>
                      <CButton
                        color='primary'
                        variant='outline'
                        onClick={() => dispatch(fetchWeatherStationHistoricalAverages())}
                        disabled={fetchingHistoricalAverages}>
                        {
                          fetchingHistoricalAverages === true ? (
                            <CSpinner size='sm' />
                          ) : null
                        }
                        {fetchingHistoricalAverages === true ? ' Loading' : 'Load Historical Averages'}
                      </CButton>
                      <CButton
                        color='primary'
                        variant='outline'
                        onClick={() => dispatch(showReportWeatherStationModal())}>
                        Report Weather Station Error
                      </CButton>
                    </div>
                    <br />
                    <div>
                      <CSmartTable
                        items={selectedWeatherStationMonthlyHistoricalAverages}
                        columns={monthlyColumns}
                        scopedColumns={scopedColumns}
                        tableProps={{
                          responsive: true,
                          striped: true,
                          hover: true,
                        }}     
                      />
                    </div>
                  </CCardBody>
                </CCard>
              </div>
            </CTabPane>
          </CTabContent>
        </CModalBody>
      </CModal>
      <CModal
        visible={reportWeatherStationModalVisible}
        backdrop='static'
        alignment='center'>
        <CModalHeader closeButton={false}>
          <CModalTitle>Report Weather Station Error</CModalTitle>
          <CButton className={styles.modalCloseButton} onClick={() => dispatch(hideReportWeatherStationModal())}>
            <CIcon className={styles.modalCloseButtonIcon} icon={cilX} />
          </CButton>
        </CModalHeader>
        <CModalBody>
          <CRow className='mb-4'>
            <CCol>
              <CFormInput
                label='Your Name'
                value={weatherStationIssueCustomerName}
                onChange={(e) => dispatch(setWeatherStationIssueCustomerName(e.target.value))}
              />
            </CCol>
          </CRow>
          <CRow className='mb-4'>
            <CCol>
              <CFormInput
                label='Weather Station Name'
                value={weatherStationNameToReport}
                onChange={(e) => dispatch(setWeatherStationNameToReport(e.target.value))}
              />
            </CCol>
          </CRow>
          <CRow className='mb-4'>
            <CCol>
              <CFormTextarea
                label='Describe the issue you are experiencing with this weather station'
                value={weatherStationIssueToReport}
                onChange={(e) => dispatch(setWeatherStationIssueToReport(e.target.value))}
              />
            </CCol>
          </CRow>
        </CModalBody>
        <CModalFooter>
          <CButton 
            color='primary' 
            onClick={() => {
              dispatch(hideReportWeatherStationModal());
              dispatch(submitWeatherStationIssue());
            }}>
            Submit Report
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
}

export default WeatherStationModal;
